import { runWithDbRequestContext } from "@crm/db/request-context";
import { serverEffectRuntime } from "@crm/effect/server/runtime";
import { handleServerError } from "@crm/errors/server-handler";
import { generateId } from "@crm/kernel/id-generator";
import { createMiddleware, createStart } from "@tanstack/react-start";
import { Data, Effect } from "effect";

class ServerFunctionNextError extends Data.TaggedError("ServerFunctionNextError")<{
  readonly cause: unknown;
}> {}

/**
 * Workbench 全局配置（拓扑与 CRM src/start.ts 对齐）：
 * requestId 在最外层生成；ServerFn 的 unknown → HTTP 终态分类唯一入口在此。
 */
const requestContextMiddleware = createMiddleware().server(({ next }) => {
  const requestId = generateId();
  return runWithDbRequestContext(() => next({ context: { requestId } }));
});

const serverFunctionErrorMiddleware = createMiddleware({
  type: "function",
}).server(({ next, context }) => {
  const requestId = (context as unknown as { requestId: string }).requestId;

  return serverEffectRuntime
    .runPromise(
      Effect.tryPromise({
        try: () => next(),
        catch: (cause) => new ServerFunctionNextError({ cause }),
      }).pipe(
        Effect.match({
          onFailure: (error) => ({ _tag: "failure" as const, error }),
          onSuccess: (value) => ({ _tag: "success" as const, value }),
        }),
      ),
    )
    .then((result) =>
      result._tag === "failure" ? handleServerError(result.error.cause, requestId) : result.value,
    );
});

export const startInstance = createStart(() => ({
  requestMiddleware: [requestContextMiddleware],
  functionMiddleware: [serverFunctionErrorMiddleware],
}));
